import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { TRAILER_ARRIVED_STATUS } from "@/lib/trailer-yard-ops";
import { toLegsWithArrivedTrailer, type LegWithArrivedTrailer } from "@/lib/order-trailer-arrival";
import type { OrderLegStatusSummary } from "@/types/tms";

/**
 * One order's legs as LegWithArrivedTrailer (for TrailerArrivedNotice), for
 * surfaces that load the order's legs but not their trailers: the order
 * detail page and side sheet. Looks up which leg trailers (by trailer number,
 * legs.trailer_id) are `arrived`. List surfaces already have trailer status
 * from their batched trailer lookup and call toLegsWithArrivedTrailer directly.
 */
export function useLegsWithArrivedTrailer(
  legs: OrderLegStatusSummary[],
  parentStatus: string | null | undefined,
): LegWithArrivedTrailer[] {
  const { tenantId } = useTenant();
  const trailerNumbers = [...new Set(legs.map((l) => l.trailer_id).filter((n): n is string => !!n))].sort();

  const { data: arrivedTrailerNumbers } = useQuery({
    queryKey: ["arrived-trailer-numbers", trailerNumbers, tenantId],
    enabled: trailerNumbers.length > 0 && !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trailers")
        .select("trailer_id")
        .in("trailer_id", trailerNumbers)
        .eq("tenant_id", tenantId!)
        .eq("current_location_status", TRAILER_ARRIVED_STATUS);
      if (error) throw error;
      return (data ?? []).map((t) => t.trailer_id);
    },
  });

  return useMemo(() => {
    const arrived = new Set(arrivedTrailerNumbers ?? []);
    return toLegsWithArrivedTrailer(legs, parentStatus, (trailerNumber) =>
      arrived.has(trailerNumber) ? TRAILER_ARRIVED_STATUS : null,
    );
  }, [legs, parentStatus, arrivedTrailerNumbers]);
}
